import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../../store/global-store/global-store';
import { useRelatedTasksTabStore, useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import { baseWhite, blueBase, blueLight, inkBase, inkLighter, skyDark } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';

export const SideSectionInitiatives = () => {
  const i18n = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const initiativePerformance = useStrategyStore((state) => state.initiativePerformance);
  const initiatives = useRelatedTasksTabStore((state) => state.selectedTask?.initiatives || []);

  const [isOpened, { toggle }] = useDisclosure(false);

  if (!initiativePerformance || initiatives.length === 0) {
    return null;
  }

  const otherInitiatives = initiatives.filter((initiative) => initiative.id !== initiativePerformance?.id);

  const handleInitiativeClick = (initiativeId: string) => {
    if (portfolio && initiativeId) {
      window.open(`/application/strategy/${portfolio.id}/${initiativeId}`, '_blank', 'noopener, noreferrer');
    }
  };

  return (
    <Stack>
      <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={toggle}>
        <Group gap={8}>
          <Icon name="folder" size={20} color={skyDark} style={{ paddingBottom: 2 }} />
          <Text size="small" weight="bold">
            {i18n.t('common.initiatives')} ({initiatives.length})
          </Text>
        </Group>
        <Icon name={isOpened ? 'unfold_less' : 'unfold_more'} size={16} color={skyDark} style={{ cursor: 'pointer' }} />
      </Group>

      {isOpened && (
        <Stack>
          <Stack gap={8}>
            <Text size="tiny" lineHeight="none" color={inkLighter}>
              {i18n.t('common.current')}
            </Text>
            <Text size="tiny" lineHeight="none" color={inkBase}>
              {initiativePerformance.name}
            </Text>
          </Stack>

          {otherInitiatives.length > 0 && (
            <Stack gap="xs">
              <Text size="tiny" lineHeight="none" color={inkLighter}>
                {i18n.t('strategy.initiatives.other')}
              </Text>
              <Group gap={8} style={{ flexWrap: 'wrap' }}>
                {otherInitiatives.map((initiative) => {
                  return (
                    <InitiativeBadge key={initiative.id} onClick={() => handleInitiativeClick(initiative.id)}>
                      <Group gap={4} style={{ flexWrap: 'nowrap' }}>
                        <Icon name="folder" size={12} color={baseWhite} style={{ flexShrink: 0 }} />
                        <Text
                          size="tiny"
                          lineHeight="none"
                          color={baseWhite}
                          style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                          }}
                        >
                          {initiative.name}
                        </Text>
                      </Group>
                    </InitiativeBadge>
                  );
                })}
              </Group>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const InitiativeBadge = styled.div`
  background-color: ${blueBase};
  border-radius: 32px;
  padding: 4px 8px;
  display: inline-flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  max-width: 100%;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${blueLight};
  }
`;
